/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL } from "../config";
import * as XLSX from "xlsx";
import { getLookupPiyasalar, getLookupPeriyotlar } from "./apiCache";

const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
};

const FAZ_RENK = {
    "Güçlü Güçlenme": "#16a34a",
    "Güçlenme":        "#22c55e",
    "Nötr":            "#d97706",
    "Zayıflama":       "#ef4444",
    "Güçlü Zayıflama": "#dc2626",
};

const fazRenk = (f) => FAZ_RENK[f] || "#94a3b8";

const skorRenk = (skor) => {
    if (skor == null) return "#94a3b8";
    if (skor >= 65) return "#16a34a";
    if (skor >= 55) return "#22c55e";
    if (skor >= 45) return "#d97706";
    if (skor >= 35) return "#ef4444";
    return "#dc2626";
};

// ─── Bilgi İçerikleri ─────────────────────────────────────────────────────────
const BILGI_ICERIK = {
    "RS Skoru": {
        baslik: "RS Skoru (Göreceli Güç)",
        icerik: "Sektörün/kategorinin, referans endekse (BIST100 veya Layer 1) göre son dönemdeki performans farkının 0-100 arası normalize edilmiş skoru.\n50 nötr eşiktir; 65 üzeri güçlü pozitif ayrışma (para girişi), 35 altı güçlü negatif ayrışma (para çıkışı) olarak yorumlanır. Ana sinyal katmanıdır.",
    },
    "Rank Değişim": {
        baslik: "Rank Değişim",
        icerik: "Sektörün/kategorinin RS skoruna göre sıralamasının (rank), önceki ölçüme göre kaç basamak değiştiği. Pozitif değer (▲) sektörün yükseldiğini, negatif değer (▼) düştüğünü gösterir. Rotasyon haritasındaki eşleştirmelerin temelini oluşturur.",
    },
    "Faz": {
        baslik: "Faz",
        icerik: "RS skorunun kendi trendine (momentumuna) göre belirlenen 5 aşamalı döngü konumu: Güçlü Güçlenme, Güçlenme, Nötr, Zayıflama, Güçlü Zayıflama.\nRS skorunun mutlak seviyesinden bağımsız olarak yönünü/ivmesini gösterir.",
    },
    "Hacim Teyit Yön": {
        baslik: "Hacim Teyit Yön",
        icerik: "Sektörün/kategorinin üye hisselerinin (veya coinlerinin) ortalama hacim akışı yönü: Pozitif, Negatif veya Nötr.\nRS skorunun işaret ettiği yönü gerçek işlem hacmiyle teyit eden ikincil katmandır.",
    },
    "Hacim Teyit Skoru": {
        baslik: "Hacim Teyit Skoru",
        icerik: "Üye hisselerin/coinlerin ortalama Hacim Akışı puanının 0-100 arası normalize hâli. RS skoruyla birlikte değerlendirildiğinde, sektöre giren/çıkan paranın gerçek işlem hacmiyle desteklenip desteklenmediğini gösterir.",
    },
    "Hacim Uyumlu": {
        baslik: "Hacim Uyumlu",
        icerik: "RS skorunun işaret ettiği yön ile Hacim Teyit Yön'ün aynı doğrultuda olup olmadığının özet göstergesi. 'Evet' ise RS hareketi hacimle teyitli, sinyal daha güvenilir. 'Hayır' ise RS hareketi henüz hacimle desteklenmiyor.",
    },
};

function BilgiIkonu({ alan }) {
    const [acik, setAcik] = useState(false);
    const bilgi = BILGI_ICERIK[alan];
    if (!bilgi) return null;
    return (
        <span style={{position:"relative",display:"inline-flex",alignItems:"center",marginLeft:4}}>
            <span
                onMouseEnter={() => setAcik(true)}
                onMouseLeave={() => setAcik(false)}
                style={{cursor:"help",color:"#94a3b8",fontSize:11,
                    border:"1px solid #334155",borderRadius:"50%",
                    width:14,height:14,display:"inline-flex",alignItems:"center",
                    justifyContent:"center",fontWeight:700,lineHeight:1}}>
                i
            </span>
            {acik && (
                <div style={{
                    position:"absolute",top:"calc(100% + 6px)",left:"50%",
                    transform:"translateX(-50%)",zIndex:500,
                    background:"#1e293b",border:"1px solid #334155",
                    borderRadius:10,padding:"12px 14px",width:270,
                    boxShadow:"0 8px 24px rgba(0,0,0,0.5)",pointerEvents:"none",
                }}>
                    <div style={{fontSize:12,fontWeight:700,color:"#3b82f6",marginBottom:6}}>{bilgi.baslik}</div>
                    <div style={{fontSize:11,color:"#94a3b8",lineHeight:1.6,whiteSpace:"pre-line"}}>{bilgi.icerik}</div>
                </div>
            )}
        </span>
    );
}

// ─── İlerleme Paneli ─────────────────────────────────────────────────────────
function TaramaIlerlemePaneli({ mesaj, ilerleme, gecen, tahminiSure }) {
    const fmt = sn => {
        if (!sn || sn <= 0) return "—";
        const m = Math.floor(sn / 60), sec = sn % 60;
        return m === 0 ? `${sec}s` : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };
    return (
        <div style={{ marginBottom: 16, background: "#1e293b", border: "2px solid #3b82f644",
            borderRadius: 12, padding: 20, boxShadow: "0 0 20px #3b82f618" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🔄</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>{mesaj || "Sektörel Güç Rotasyonu Çalışıyor"}</span>
                <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: "#3b82f6" }}>%{ilerleme}</span>
            </div>
            <div style={{ height: 10, background: "#0f172a", borderRadius: 6, overflow: "hidden",
                marginBottom: 10, border: "1px solid #334155" }}>
                <div style={{ height: "100%", width: `${ilerleme}%`, background: "linear-gradient(90deg,#3b82f6,#06b6d4)",
                    borderRadius: 6, transition: "width 0.5s ease" }} />
            </div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>GEÇEN SÜRE</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#0d9488" }}>{fmt(gecen)}</div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>TAHMİNİ KALAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#eab308" }}>
                        {tahminiSure != null ? fmt(tahminiSure) : "Hesaplanıyor..."}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Hareketli Yükleme Çubuğu ────────────────────────────────────────────────
function HareketliBar() {
    const [offset, setOffset] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setOffset(o => (o+3)%200), 20);
        return () => clearInterval(id);
    }, []);
    const pos = offset<=100 ? offset-100 : 200-offset-100;
    return (
        <div style={{height:8,background:"#0f172a",borderRadius:4,overflow:"hidden",
            border:"1px solid #334155",marginBottom:16,position:"relative"}}>
            <div style={{position:"absolute",top:0,height:"100%",width:"40%",
                left:`${50+pos*0.5}%`,
                background:"linear-gradient(90deg,transparent,#3b82f6,#06b6d4,#3b82f6,transparent)",
                borderRadius:4}}/>
        </div>
    );
}

// ─── İndir Alt Menüsü ─────────────────────────────────────────────────────────
function IndirMenusu({ veriler, setIslemMenuAcik }) {
    const [acik, setAcik] = useState(false);
    const dosyaAdi = `sektorel_rotasyon_${new Date().toLocaleDateString("tr-TR").replace(/\./g,"-")}`;
    const indir = async (fmt) => {
        setAcik(false); setIslemMenuAcik(false);
        const veri = veriler();
        if (!veri?.length) return;
        if (fmt === "xlsx") {
            const ws = XLSX.utils.json_to_sheet(veri);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sektörel Rotasyon");
            XLSX.writeFile(wb, `${dosyaAdi}.xlsx`);
        } else if (fmt === "csv") {
            const h = Object.keys(veri[0] || {});
            const rows = veri.map(r => h.map(k => { const v = String(r[k] ?? ""); return v.includes(",")||v.includes('"') ? `"${v.replace(/"/g,'""')}"` : v; }).join(","));
            const blob = new Blob(["\uFEFF"+[h.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.csv`; a.click(); URL.revokeObjectURL(url);
        } else if (fmt === "pdf") {
            try {
                const { default: jsPDF } = await import("jspdf");
                const { default: autoTable } = await import("jspdf-autotable");
                const h = Object.keys(veri[0] || {});
                const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
                doc.setFontSize(14); doc.text("Sektörel Güç Rotasyonu Sonuçları", 20, 30);
                doc.setFontSize(9); doc.setTextColor(100);
                doc.text(`${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}`, 20, 48);
                autoTable(doc, { head: [h], body: veri.map(r => h.map(k => String(r[k] ?? ""))), startY: 58,
                    styles: { fontSize: 7, cellPadding: 3 },
                    headStyles: { fillColor: [29,78,216], textColor: 255, fontStyle: "bold" },
                    alternateRowStyles: { fillColor: [245,247,250] } });
                doc.save(`${dosyaAdi}.pdf`);
            } catch { toast.error("PDF oluşturulamadı"); indir("svg"); }
        } else if (fmt === "svg") {
            const h = Object.keys(veri[0] || {});
            const cw = Math.max(60, Math.floor(740/h.length)), rh = 18, hh = 24, tw = cw*h.length;
            const pw = Math.max(tw+40, 800), ph = 80 + hh + veri.length*rh;
            const esc = s => String(s).slice(0,18).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
            const rows = veri.map((r,i) => `<g transform="translate(20,${60+hh+i*rh})"><rect width="${tw}" height="${rh}" fill="${i%2===0?"#1e293b":"#0f172a"}" stroke="#334155" stroke-width="0.5"/>${h.map((k,j) => `<text x="${j*cw+4}" y="13" font-size="9" fill="#e2e8f0" font-family="Arial">${esc(r[k])}</text>`).join("")}</g>`).join("");
            const heads = h.map((k,j) => `<text x="${20+j*cw+4}" y="48" font-size="9" font-weight="bold" fill="white" font-family="Arial">${esc(k)}</text>`).join("");
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${pw}" height="${ph}"><rect width="${pw}" height="${ph}" fill="#0f172a"/><text x="20" y="22" font-size="14" font-weight="bold" fill="#e2e8f0" font-family="Arial">Sektörel Güç Rotasyonu Sonuçları</text><text x="20" y="38" font-size="10" fill="#94a3b8" font-family="Arial">${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}</text><rect x="20" y="52" width="${tw}" height="${hh}" fill="#1d4ed8"/>${heads}${rows}</svg>`;
            const blob = new Blob([svg], { type: "image/svg+xml" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.svg`; a.click(); URL.revokeObjectURL(url);
        }
    };
    return (
        <div style={{ position: "relative" }} onMouseEnter={() => setAcik(true)} onMouseLeave={() => setAcik(false)}>
            <button style={{ display: "flex", width: "100%", textAlign: "left", justifyContent: "space-between",
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                color: "#e2e8f0", fontSize: 13, borderBottom: "1px solid #334155" }}>
                📥 İndir <span style={{ color: "#94a3b8" }}>▶</span>
            </button>
            {acik && (
                <div style={{ position: "absolute", left: "100%", top: 0, background: "#1e293b",
                    border: "1px solid #334155", borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)", minWidth: 160, overflow: "hidden", zIndex: 101 }}>
                    {[{l:"Excel (.xlsx)",f:"xlsx"},{l:"CSV (.csv)",f:"csv"},{l:"PDF (.pdf)",f:"pdf"},{l:"SVG (.svg)",f:"svg"}].map(b => (
                        <button key={b.f} onClick={() => indir(b.f)}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
                                border: "none", background: "none", cursor: "pointer", color: "#e2e8f0", fontSize: 13,
                                borderBottom: "1px solid #334155" }}>{b.l}</button>
                    ))}
                </div>
            )}
        </div>
    );
}

const COKLU_MODULLER = [
    { label: "📊 Dip Tarama",           value: "Dip Tarama",           pasif: false },
    { label: "🌊 Likidite Tarama",       value: "Likidite Tarama",      pasif: false },
    { label: "📈 FVG Tarama",            value: "FVG Tarama",           pasif: false },
    { label: "⚓ Çapa Noktası Tarama",   value: "Çapa Noktası Tarama",  pasif: false },
    { label: "📐 MSB Tarama",            value: "MSB Tarama",           pasif: false },
    { label: "🎯 OTE Tarama",            value: "OTE Tarama",           pasif: false },
    { label: "🧱 OB/BB Tarama",          value: "OB & BB Tarama",       pasif: false },
    { label: "🎣 IDM Tarama",            value: "IDM Tarama",           pasif: false },
    { label: "⚖️ P/D Zone Tarama",       value: "P/D Zone Tarama",      pasif: false },
    { label: "🔀 SMT Tarama",            value: "SMT Tarama",           pasif: false },
    { label: "⏰ Killzone Tarama",        value: "Killzone Tarama",      pasif: false },
    { label: "🌿 Topdown Cascade",        value: "Topdown Cascade",      pasif: false },
    { label: "📊 Hacim Profili Tarama",   value: "Hacim Profili Tarama", pasif: false },
    { label: "🌊 Hacim Akışı Tarama",     value: "Hacim Akışı Tarama",   pasif: false },
    { label: "📉 Divergence Tarama",      value: "Divergence Tarama",    pasif: false },
];

const _PERIYOT_OPTS_CK = ["1 Gün", "1 Hafta", "1 Ay", "3 Aylık", "6 Aylık", "Yıllık"];
const _KZ_LISTESI_CK   = [
    "Asya KZ (03:00-09:00)", "Londra Açılış (10:00-12:00)", "New York Açılış (15:00-17:00)",
    "Londra Kapanış (18:00-20:00)", "BIST Açılış (10:00-11:00)",
    "BIST Öğle Dönüşü (13:00-14:00)", "BIST Kapanış Öncesi (17:00-18:00)",
];

const MODUL_KEY_MAP = {"Dip Tarama":"dip_tarama","Likidite Tarama":"likidite_tarama","FVG Tarama":"fvg_tarama","Çapa Noktası Tarama":"capa_noktasi_tarama","MSB Tarama":"msb_tarama","OTE Tarama":"ote_tarama","OB & BB Tarama":"obbb_tarama","IDM Tarama":"idm_tarama","P/D Zone Tarama":"pd_zone_tarama","SMT Tarama":"smt_tarama","Killzone Tarama":"killzone_tarama","Topdown Cascade":"topdown_cascade_tarama","Hacim Profili Tarama":"hacim_profili_tarama","Hacim Akışı Tarama":"hacim_akisi_tarama","Divergence Tarama":"divergence_tarama"};

function defaultCokluFiltreler(modul, piyasa) {
    switch (modul) {
        case "Dip Tarama":       return { tarama_periyot: "Aylık", tolerans: 1 };
        case "Likidite Tarama":  return { sweep_tipler: { "Boğa Sweep": true, "Ayı Sweep": false }, tarama_periyot: "1 Gün", lookback: 20, wick_oran: 50 };
        case "FVG Tarama":       return { fvg_tipler: { "Boğa FVG": true, "Ayı FVG": false, "iFVG": false }, bpr_kontrol: "Hayır", tarama_periyot: "1 Gün", mum_sayisi: 100 };
        case "Çapa Noktası Tarama": return { fvg_tipler: { "Boğa FVG": true, "Ayı FVG": false }, tolerans: 30 };
        case "MSB Tarama":       return { msb_tipler: { "Boğa MSB": true, "Ayı MSB": false }, tarama_periyot: "1 Ay", swing_pencere: 3 };
        case "OTE Tarama":       return { ote_tipler: { "Boğa OTE": true, "Ayı OTE": false }, tarama_periyot: "1 Hafta", swing_pencere: 3, mum_sayisi: 100 };
        case "OB & BB Tarama":   return { obbb_tipler: { "Boğa OB": true, "Ayı OB": false, "Boğa BB": false, "Ayı BB": false }, tarama_periyot: "1 Gün", mum_sayisi: 200, govde_oran: 60, sadece_taze: true };
        case "IDM Tarama":       return { idm_tipler: { "Boğa IDM": true, "Ayı IDM": false }, tarama_periyot: "1 Gün", mum_sayisi: 200 };
        case "P/D Zone Tarama":  return { bolge_filtre: { "Discount": true, "Equilibrium": false, "Premium": false }, tarama_periyot: "1 Gün", range_window: 20, mum_sayisi: 200 };
        case "SMT Tarama": return { smt_tipler: { "Boğa SMT": true, "Ayı SMT": false }, tarama_periyot: "1 Gün", referans_varlik: piyasa === "BIST" ? "XU100 (BIST100)" : "BTC", swing_pencere: 3, kor_pencere: 50, min_korelasyon: 70, mum_sayisi: 200 };
        case "Killzone Tarama":  return { killzoneler: { "BIST Açılış (10:00-11:00)": true }, yapi_tipleri: { "FVG": true, "MSB": false, "Sweep": false }, geri_bak_gun: 5 };
        case "Topdown Cascade":   return { topdown_mod: piyasa };
        case "Hacim Profili Tarama": return { tarama_periyot: "1 Gün", sinyal_filtre: [], min_skor: 0, mum_sayisi: 300 };
        case "Hacim Akışı Tarama": return { lookback: 20, min_spike: 1.5, sinyal_filtre: {"📈 Güçlü Alım":true,"🔶 Orta Alım":true,"⬇️ Zayıf Alım":false,"📉 Güçlü Satım":true,"🔶 Orta Satım":true,"⬆️ Zayıf Satım":false,"⚖️ Belirsiz":false} };
        case "Divergence Tarama": return { tarama_periyot: "1 Gün", pivot_modu: "fractal", osilatorler: {"RSI":true,"MACD Histogram":true,"Stokastik RSI":true,"OBV":false}, divergence_tipleri: {"Regular Bullish":true,"Regular Bearish":true,"Hidden Bullish":false,"Hidden Bearish":false}, swing_pencere:5, zigzag_esik:1, n_son_pivot:2, min_pivot_mesafe:3, min_hareket_atr:0, rsi_periyot:14, macd_fast:12, macd_slow:26, macd_signal:9, stoch_rsi_periyot:14, stoch_periyot:14, stoch_k:3, stoch_d:3, ayarlar_acik:false };
        default: return {};
    }
}

function CokluTaramaGonderPopup({ piyasa, paraBirimi, secilenSayisi, secilenModul, filtre, periyotlar, onModulSec, onFiltreDegis, onIptal, onBaslat }) {
    const periyotOpts = periyotlar && periyotlar.length > 0 ? periyotlar : _PERIYOT_OPTS_CK;
    const set = (key, val) => onFiltreDegis({ ...filtre, [key]: val });
    const ck  = (grup, key) => onFiltreDegis({ ...filtre, [grup]: { ...filtre[grup], [key]: !filtre[grup][key] } });

    const CheckGrup = ({ alan, etiket }) => (
        <div style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#93c5fd", marginBottom: "6px" }}>{etiket}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {Object.keys(filtre[alan] || {}).map(k => (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer" }}>
                        <input type="checkbox" checked={!!filtre[alan][k]} onChange={() => ck(alan, k)} />{k}
                    </label>
                ))}
            </div>
        </div>
    );

    const Sel = ({ alan, etiket, opts, labelFn }) => (
        <div style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#93c5fd", marginBottom: "6px" }}>{etiket}</div>
            <select value={filtre[alan]} onChange={e => set(alan, isNaN(e.target.value) ? e.target.value : Number(e.target.value))}
                style={{ padding: "6px 10px", borderRadius: "7px", border: "1px solid #334155", background: "#0f172a", color: "#e2e8f0", fontSize: "13px", width: "100%" }}>
                {opts.map(o => <option key={o} value={o}>{labelFn ? labelFn(o) : o}</option>)}
            </select>
        </div>
    );

    const renderFiltreler = () => {
        if (!filtre) return null;
        switch (secilenModul) {
            case "Dip Tarama":       return (<><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="tolerans" etiket="Tolerans" opts={[1,3,5]} labelFn={v=>`%${v}`} /></>);
            case "Likidite Tarama":  return (<><CheckGrup alan="sweep_tipler" etiket="Sweep Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="lookback" etiket="Lookback" opts={[10,20,50,100]} labelFn={v=>`${v} Mum`} /><Sel alan="wick_oran" etiket="Min Fitil" opts={[30,40,50,60,70]} labelFn={v=>`%${v}`} /></>);
            case "FVG Tarama":       return (<><CheckGrup alan="fvg_tipler" etiket="FVG Tipi" /><Sel alan="bpr_kontrol" etiket="BPR Kontrol" opts={["Evet","Hayır"]} /><Sel alan="tarama_periyot" etiket="Tarama Periyodu" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[25,50,100,150,200,250,300,350,400,500,600,700]} /></>);
            case "Çapa Noktası Tarama": return (<><CheckGrup alan="fvg_tipler" etiket="FVG Tipi" /><Sel alan="tolerans" etiket="Tolerans" opts={[30,50,70,80,90,100]} labelFn={v=>`%${v}`} /></>);
            case "MSB Tarama":       return (<><CheckGrup alan="msb_tipler" etiket="MSB Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,4,5,6,7,8]} labelFn={v=>`${v} Mum`} /></>);
            case "OTE Tarama":       return (<><CheckGrup alan="ote_tipler" etiket="OTE Tipi" /><Sel alan="tarama_periyot" etiket="Tarama Periyodu" opts={periyotOpts} /><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,4,5,6,7,8]} labelFn={v=>`${v} Mum`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[50,100,150,200,300]} /></>);
            case "OB & BB Tarama":   return (<><CheckGrup alan="obbb_tipler" etiket="OB/BB Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /><Sel alan="govde_oran" etiket="Min Gövde" opts={[40,50,60,70]} labelFn={v=>`%${v}`} /><div style={{marginBottom:"10px"}}><label style={{display:"flex",alignItems:"center",gap:"6px",fontSize:"12px",cursor:"pointer"}}><input type="checkbox" checked={filtre.sadece_taze} onChange={e=>set("sadece_taze",e.target.checked)} />Sadece Taze</label></div></>);
            case "IDM Tarama":       return (<><CheckGrup alan="idm_tipler" etiket="IDM Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /></>);
            case "P/D Zone Tarama":  return (<><CheckGrup alan="bolge_filtre" etiket="Bölge" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="range_window" etiket="Range Penceresi" opts={[10,20,50,100]} labelFn={v=>`${v} Mum`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /></>);
            case "SMT Tarama": return (<><CheckGrup alan="smt_tipler" etiket="SMT Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Referans Varlık</div><select value={filtre.referans_varlik} onChange={e=>set("referans_varlik",e.target.value)} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"100%"}}>{(piyasa==="BIST"?["XU100 (BIST100)","XU030 (BIST30)"]:["BTC","ETH","BNB"]).map(v=><option key={v}>{v}</option>)}</select></div><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,5]} labelFn={v=>`${v} Mum`} /><Sel alan="min_korelasyon" etiket="Min Korelasyon" opts={[50,60,70,80,90]} labelFn={v=>`%${v}`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300]} /></>);
            case "Killzone Tarama":  return (<><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Killzone</div><div style={{display:"flex",flexWrap:"wrap",gap:"8px"}}>{_KZ_LISTESI_CK.filter(k=>piyasa==="BIST"||!k.includes("BIST")).map(k=><label key={k} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer"}}><input type="checkbox" checked={!!filtre.killzoneler[k]} onChange={()=>ck("killzoneler",k)} />{k.split("(")[0].trim()}</label>)}</div></div><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Yapı Tipi</div><div style={{display:"flex",gap:"12px"}}>{["FVG","MSB","Sweep"].map(k=><label key={k} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer"}}><input type="checkbox" checked={!!filtre.yapi_tipleri[k]} onChange={()=>ck("yapi_tipleri",k)} />{k}</label>)}</div></div><Sel alan="geri_bak_gun" etiket="Geriye Bakış" opts={[1,3,5,10]} labelFn={v=>`Son ${v} Gün`} /></>);
            case "Topdown Cascade": return (<div style={{fontSize:"13px",color:"#94a3b8",padding:"8px 0"}}>{piyasa} — EMA200 + 4H Sweep taraması. Ek kriter seçimi gerekmez.</div>);
            case "Hacim Profili Tarama": {
                const HP_SF = ["POC Yakını","Discount","Premium","HVN Yakın","LVN İçi"];
                const hpSF = filtre.sinyal_filtre || [];
                const toggleHpSF = v => { const sf = Array.isArray(hpSF)?hpSF:[]; set("sinyal_filtre", sf.includes(v)?sf.filter(x=>x!==v):[...sf,v]); };
                return (<>
                    <Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} />
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Sinyal Filtresi</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>{HP_SF.map(v=>{const aktif=Array.isArray(hpSF)&&hpSF.includes(v);return(<button key={v} type="button" onClick={()=>toggleHpSF(v)} style={{padding:"3px 10px",borderRadius:20,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f622":"transparent",color:aktif?"#3b82f6":"#94a3b8"}}>{v}</button>);})}</div>
                    </div>
                    <Sel alan="min_skor" etiket="Min Skor" opts={[0,2,3,4,5,6,7]} labelFn={v=>v===0?"Tümü":`≥${v}`} />
                    <Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} />
                </>);
            }
            case "Hacim Akışı Tarama": {
                const HA_RENK_MAP = {"📈 Güçlü Alım":"#16a34a","🔶 Orta Alım":"#84cc16","⬇️ Zayıf Alım":"#a3e635","📉 Güçlü Satım":"#dc2626","🔶 Orta Satım":"#f97316","⬆️ Zayıf Satım":"#fca5a5","⚖️ Belirsiz":"#9ca3af"};
                const haSF = (typeof filtre.sinyal_filtre==="object"&&!Array.isArray(filtre.sinyal_filtre)) ? filtre.sinyal_filtre : {};
                const toggleHA = s => set("sinyal_filtre",{...haSF,[s]:!haSF[s]});
                return (<>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Ort. Pencere (Gün)</div>
                        <select value={filtre.lookback||20} onChange={e=>set("lookback",Number(e.target.value))} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"100%"}}>
                            {[5,10,20,30,40,50].map(v=><option key={v} value={v}>{v} Gün</option>)}
                        </select>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Min. Spike (×Ort.)</div>
                        <input type="number" value={filtre.min_spike||1.5} min={1.0} max={10} step={0.1} onChange={e=>set("min_spike",parseFloat(e.target.value))} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"70px"}}/>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Sinyal Tipi</div>
                        <div style={{display:"flex",gap:8}}>
                            <div style={{background:"#0f172a",border:"1px solid #16a34a55",borderRadius:6,padding:"6px 10px",flex:1}}>
                                <div style={{fontSize:10,fontWeight:700,color:"#16a34a",marginBottom:4}}>📈 ALIMLAR</div>
                                {["📈 Güçlü Alım","🔶 Orta Alım","⬇️ Zayıf Alım","⚖️ Belirsiz"].map(s=>{const aktif=!!haSF[s];return(<button key={s} type="button" onClick={()=>toggleHA(s)} style={{display:"block",width:"100%",textAlign:"left",padding:"2px 6px",borderRadius:4,marginBottom:2,cursor:"pointer",fontSize:11,fontWeight:aktif?700:400,background:aktif?HA_RENK_MAP[s]+"22":"transparent",border:`1px solid ${aktif?HA_RENK_MAP[s]:"transparent"}`,color:aktif?HA_RENK_MAP[s]:"#94a3b8"}}>{s}</button>);})}
                            </div>
                            <div style={{background:"#0f172a",border:"1px solid #dc262655",borderRadius:6,padding:"6px 10px",flex:1}}>
                                <div style={{fontSize:10,fontWeight:700,color:"#dc2626",marginBottom:4}}>📉 SATIMLAR</div>
                                {["📉 Güçlü Satım","🔶 Orta Satım","⬆️ Zayıf Satım"].map(s=>{const aktif=!!haSF[s];return(<button key={s} type="button" onClick={()=>toggleHA(s)} style={{display:"block",width:"100%",textAlign:"left",padding:"2px 6px",borderRadius:4,marginBottom:2,cursor:"pointer",fontSize:11,fontWeight:aktif?700:400,background:aktif?HA_RENK_MAP[s]+"22":"transparent",border:`1px solid ${aktif?HA_RENK_MAP[s]:"transparent"}`,color:aktif?HA_RENK_MAP[s]:"#94a3b8"}}>{s}</button>);})}
                            </div>
                        </div>
                    </div>
                </>);
            }
            case "Divergence Tarama": {
                const DIV_OSC = ["RSI","MACD Histogram","Stokastik RSI","OBV"];
                const DIV_TIPLER = [{key:"Regular Bullish",ikon:"📈",renk:"#16a34a"},{key:"Regular Bearish",ikon:"📉",renk:"#dc2626"},{key:"Hidden Bullish",ikon:"🔼",renk:"#06b6d4"},{key:"Hidden Bearish",ikon:"🔽",renk:"#f59e0b"}];
                const divOsc = filtre.osilatorler || {};
                const divTip = filtre.divergence_tipleri || {};
                const toggleOsc = o => set("osilatorler",{...divOsc,[o]:!divOsc[o]});
                const toggleTip = t => set("divergence_tipleri",{...divTip,[t]:!divTip[t]});
                const ayarlarAcik = filtre.ayarlar_acik||false;
                const stochAktif = !!divOsc["Stokastik RSI"], macdAktif = !!divOsc["MACD Histogram"], rsiAktif = !!divOsc["RSI"];
                return (<>
                    <Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} />
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Pivot Modu</div>
                        <div style={{display:"flex",gap:6}}>{[["fractal","Fractal"],["zigzag","ZigZag"]].map(([v,l])=>{const aktif=(filtre.pivot_modu||"fractal")===v;return(<button key={v} type="button" onClick={()=>set("pivot_modu",v)} style={{padding:"5px 12px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f622":"#0f172a",color:aktif?"#3b82f6":"#94a3b8"}}>{l}</button>);})}</div>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Osilatörler</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{DIV_OSC.map(o=>{const aktif=!!divOsc[o];return(<button key={o} type="button" onClick={()=>toggleOsc(o)} style={{padding:"4px 10px",borderRadius:8,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f618":"#0f172a",color:aktif?"#3b82f6":"#94a3b8"}}>{o}</button>);})}</div>
                    </div>
                    <div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Divergence Tipleri</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{DIV_TIPLER.map(t=>{const aktif=!!divTip[t.key];return(<button key={t.key} type="button" onClick={()=>toggleTip(t.key)} style={{padding:"4px 10px",borderRadius:8,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?t.renk:"#334155"}`,background:aktif?t.renk+"18":"#0f172a",color:aktif?t.renk:"#94a3b8"}}>{t.ikon} {t.key}</button>);})}</div>
                    </div>
                    <button type="button" onClick={()=>set("ayarlar_acik",!ayarlarAcik)} style={{padding:"5px 12px",borderRadius:8,border:"1px solid #334155",background:ayarlarAcik?"#334155":"#0f172a",color:"#e2e8f0",fontSize:11,fontWeight:600,cursor:"pointer",marginBottom:8}}>⚙️ Gelişmiş Ayarlar {ayarlarAcik?"▲":"▼"}</button>
                    {ayarlarAcik && (<div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:8}}>
                        {(filtre.pivot_modu||"fractal")==="fractal"
                            ? (<div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>Swing Penceresi</div><input type="number" value={filtre.swing_pencere||5} min={2} max={20} onChange={e=>set("swing_pencere",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>)
                            : (<div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>ZigZag Eşik (%)</div><input type="number" value={filtre.zigzag_esik||1} min={0.5} max={20} step={0.5} onChange={e=>set("zigzag_esik",parseFloat(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>)
                        }
                        <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>N Son Pivot</div><div style={{display:"flex",gap:4}}>{[2,3].map(n=>{const a=(filtre.n_son_pivot||2)===n;return(<button key={n} type="button" onClick={()=>set("n_son_pivot",n)} style={{padding:"4px 8px",borderRadius:6,fontSize:12,cursor:"pointer",border:`1px solid ${a?"#3b82f6":"#334155"}`,background:a?"#3b82f622":"#0f172a",color:a?"#3b82f6":"#94a3b8"}}>{n}</button>);})}</div></div>
                        <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>Min Pivot Mesafe</div><input type="number" value={filtre.min_pivot_mesafe||3} min={0} max={20} onChange={e=>set("min_pivot_mesafe",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>
                        <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>Min Hareket (xATR)</div><input type="number" value={filtre.min_hareket_atr||0} min={0} max={5} step={0.1} onChange={e=>set("min_hareket_atr",parseFloat(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>
                        {rsiAktif && <div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>RSI Periyot</div><input type="number" value={filtre.rsi_periyot||14} min={2} max={50} onChange={e=>set("rsi_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>}
                        {macdAktif && <><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Fast</div><input type="number" value={filtre.macd_fast||12} min={2} max={50} onChange={e=>set("macd_fast",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Slow</div><input type="number" value={filtre.macd_slow||26} min={5} max={100} onChange={e=>set("macd_slow",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Signal</div><input type="number" value={filtre.macd_signal||9} min={2} max={50} onChange={e=>set("macd_signal",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div></>}
                        {stochAktif && <><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>StochRSI RSI Per.</div><input type="number" value={filtre.stoch_rsi_periyot||14} min={2} max={50} onChange={e=>set("stoch_rsi_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>StochRSI Stoch Per.</div><input type="number" value={filtre.stoch_periyot||14} min={2} max={50} onChange={e=>set("stoch_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>%K</div><input type="number" value={filtre.stoch_k||3} min={1} max={20} onChange={e=>set("stoch_k",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:55}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>%D</div><input type="number" value={filtre.stoch_d||3} min={1} max={20} onChange={e=>set("stoch_d",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:55}}/></div></>}
                    </div>)}
                </>);
            }
            default: return null;
        }
    };

    return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
            <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:"14px",padding:"1.75rem",width:"500px",maxWidth:"95vw",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                <div style={{fontSize:"18px",fontWeight:"700",color:"#e2e8f0",marginBottom:"0.5rem"}}>🔗 Çoklu Taramaya Gönder</div>
                <p style={{fontSize:"13px",color:"#94a3b8",marginBottom:"1.25rem"}}>
                    <strong>{secilenSayisi}</strong> seçili. Devam edilecek modülü ve kriterleri seçin.
                </p>
                <div style={{marginBottom:"1.25rem"}}>
                    <div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"8px"}}>Modül Seçin</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
                        {COKLU_MODULLER.map(m => (
                            <button key={m.value} type="button" disabled={m.pasif} onClick={() => !m.pasif && onModulSec(m.value)}
                                style={{padding:"8px 10px",borderRadius:"8px",border:"2px solid",
                                    borderColor:secilenModul===m.value?"#3b82f6":m.pasif?"#1e293b":"#334155",
                                    background: secilenModul===m.value?"#1d4ed822":m.pasif?"#0f172a":"#0f172a",
                                    color:m.pasif?"#475569":secilenModul===m.value?"#93c5fd":"#e2e8f0",
                                    cursor:m.pasif?"default":"pointer",
                                    fontSize:"12px",fontWeight:secilenModul===m.value?"600":"400",textAlign:"left"}}>
                                {m.label}
                                {m.pasif && <span style={{fontSize:"10px",marginLeft:"4px",color:"#d1d5db"}}>(Mevcut)</span>}
                            </button>
                        ))}
                    </div>
                </div>
                {secilenModul && (
                    <div style={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px",padding:"10px 14px",marginBottom:"1rem",display:"flex",gap:"20px"}}>
                        <div><div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"500"}}>Piyasa</div><div style={{fontSize:"13px",fontWeight:"600",color:"#e2e8f0"}}>🔒 {piyasa}</div></div>
                        <div><div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"500"}}>Para Birimi</div><div style={{fontSize:"13px",fontWeight:"600",color:"#e2e8f0"}}>🔒 {paraBirimi}</div></div>
                    </div>
                )}
                {secilenModul && filtre && (
                    <div style={{marginBottom:"1.25rem"}}>
                        <div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"10px"}}>Tarama Kriterleri</div>
                        {renderFiltreler()}
                    </div>
                )}
                <div style={{display:"flex",gap:"8px",justifyContent:"flex-end"}}>
                    <button onClick={onIptal} style={{padding:"8px 18px",borderRadius:"8px",border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:"13px",color:"#94a3b8"}}>İptal</button>
                    <button onClick={onBaslat} disabled={!secilenModul}
                        style={{padding:"8px 20px",borderRadius:"8px",border:"none",background:secilenModul?"linear-gradient(135deg,#1d4ed8,#3b82f6)":"#475569",color:"white",cursor:secilenModul?"pointer":"default",fontSize:"13px",fontWeight:"600"}}>
                        Taramayı Başlat →
                    </button>
                </div>
            </div>
        </div>
    );
}

function DetayPanel({ r, piyasa, onKapat }) {
    if (!r) return null;
    return (
        <>
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 250 }} onClick={onKapat} />
            <div style={{
                position: "fixed", top: 0, right: 0, bottom: 0, width: 460, maxWidth: "94vw",
                background: "#1e293b", borderLeft: "1px solid #334155",
                boxShadow: "-12px 0 32px rgba(0,0,0,0.5)", zIndex: 300,
                overflowY: "auto", padding: "1.5rem",
            }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Detay</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0" }}>{r["Endeks"]}</div>
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>{r["Ad"]}</div>
                    </div>
                    <button onClick={onKapat} style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 20, cursor: "pointer", padding: 4 }}>✕</button>
                </div>

                <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                    <div style={{ flex: 1, background: "#0f172a", borderRadius: 8, padding: "10px 12px", textAlign: "center", border: `2px solid ${skorRenk(r["RS Skoru"])}44` }}>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>RS SKORU</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: skorRenk(r["RS Skoru"]) }}>{r["RS Skoru"]}/100</div>
                    </div>
                    <div style={{ flex: 1, background: "#0f172a", borderRadius: 8, padding: "10px 12px", textAlign: "center", border: `2px solid ${fazRenk(r["Faz"])}44` }}>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>FAZ</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: fazRenk(r["Faz"]) }}>{r["Faz"]}</div>
                    </div>
                </div>

                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: "1px solid #334155" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#93c5fd", marginBottom: 10 }}>📊 RS Skoru Nasıl Hesaplanır?</div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#1e293b", borderRadius: 8, padding: "8px 12px", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>Rank (sıralama)</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0" }}>#{r["Rank"]}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#1e293b", borderRadius: 8, padding: "8px 12px", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>Rank Değişim</span>
                        <span style={{ fontSize: 14, fontWeight: 700,
                            color: (r["Rank Değişim"]||0) > 0 ? "#16a34a" : (r["Rank Değişim"]||0) < 0 ? "#dc2626" : "#94a3b8" }}>
                            {r["Rank Değişim"] > 0 ? `▲ +${r["Rank Değişim"]}` : r["Rank Değişim"] < 0 ? `▼ ${r["Rank Değişim"]}` : "—"}
                        </span>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.6, marginTop: 8 }}>
                        <strong style={{color:"#e2e8f0"}}>RS Skoru</strong>, bu {piyasa==="BIST"?"sektörün":"kategorinin"} referans endekse
                        ({piyasa==="BIST"?"BIST100":"Layer 1"}) göre son dönemdeki göreceli performansının 0-100 arası normalize edilmiş hâlidir.
                        Tüm {piyasa==="BIST"?"sektörler":"kategoriler"} bu skora göre <strong style={{color:"#e2e8f0"}}>Rank</strong> alır;
                        <strong style={{color:"#e2e8f0"}}> Rank Değişim</strong> önceki ölçüme göre kaç basamak yükselip/düştüğünü gösterir ve rotasyon haritasındaki
                        eşleştirmelerin temelini oluşturur.
                    </div>
                </div>

                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: "1px solid #334155" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#93c5fd", marginBottom: 10 }}>🌀 Faz Nasıl Belirlenir?</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>
                        RS Skoru'nun mutlak seviyesi değil, <strong style={{color:"#e2e8f0"}}>kendi trendi (momentumu)</strong> ölçülerek 5 aşamalı bir
                        döngüye yerleştirilir: <strong style={{color:"#16a34a"}}>Güçlü Güçlenme</strong> → <strong style={{color:"#22c55e"}}>Güçlenme</strong> →
                        <strong style={{color:"#d97706"}}> Nötr</strong> → <strong style={{color:"#ef4444"}}>Zayıflama</strong> → <strong style={{color:"#dc2626"}}>Güçlü Zayıflama</strong>.
                        RS skoru düşük olsa bile "Güçlenme" fazındaki bir {piyasa==="BIST"?"sektör":"kategori"} toparlanma sinyali;
                        RS skoru yüksek olsa bile "Zayıflama" fazındaki bir {piyasa==="BIST"?"sektör":"kategori"} olası bir tepe sinyali verebilir.
                    </div>
                </div>

                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: "1px solid #334155" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#93c5fd", marginBottom: 10 }}>📈 Hacim Teyidi Nasıl Belirlenir?</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#1e293b", borderRadius: 8, padding: "8px 12px" }}>
                            <span style={{ fontSize: 12, color: "#94a3b8" }}>Hacim Teyit Yön</span>
                            <span style={{ fontSize: 13, fontWeight: 700,
                                color: r["Hacim Teyit Yön"] === "Pozitif" ? "#16a34a" : r["Hacim Teyit Yön"] === "Negatif" ? "#dc2626" : "#94a3b8" }}>
                                {r["Hacim Teyit Yön"] || "—"}
                            </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#1e293b", borderRadius: 8, padding: "8px 12px" }}>
                            <span style={{ fontSize: 12, color: "#94a3b8" }}>Hacim Teyit Skoru</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{r["Hacim Teyit Skoru"] ?? "—"}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#1e293b", borderRadius: 8, padding: "8px 12px" }}>
                            <span style={{ fontSize: 12, color: "#94a3b8" }}>Hacim Uyumlu mu?</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: r["Hacim Uyumlu"]?.includes("Evet") ? "#16a34a" : "#94a3b8" }}>
                                {r["Hacim Uyumlu"] || "—"}
                            </span>
                        </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.6, marginTop: 10 }}>
                        Üye {piyasa==="BIST"?"hisselerin":"coinlerin"} ortalama <strong style={{color:"#e2e8f0"}}>Hacim Akışı</strong> puanı hesaplanarak
                        RS skorunun işaret ettiği yönle karşılaştırılır. İkisi aynı yöndeyse
                        <strong style={{color:"#16a34a"}}> Hacim Uyumlu: Evet</strong> — bu, RS hareketinin gerçek alım/satım hacmiyle desteklendiğine
                        işaret eder. Uyumsuzsa RS hareketi henüz hacimle teyit edilmemiş demektir.
                    </div>
                </div>

                <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
                    {piyasa === "BIST" ? "BIST" : "Kripto"} · Sektörel Güç Rotasyonu metodolojisi
                </div>
            </div>
        </>
    );
}

export default function SektorelRotasyonTarama() {
    const navigate = useNavigate();

    const [durum,      setDurum]      = useState(null);
    const [sonuc,      setSonuc]      = useState(null);
    const [calisiyor,  setCalisiyor]  = useState(false);
    const [ilerleme,   setIlerleme]   = useState(0);
    const [tahminiSure,setTahminiSure]= useState(null);
    const [gecenSure,  setGecenSure]  = useState(0);
    const [baslangicZamani, setBaslangicZamani] = useState(null);
    const [siralamaKol,setSiralamaKol]= useState("RS Skoru");
    const [siralamaYon,setSiralamaYon]= useState("desc");
    const [piyasa,     setPiyasa]     = useState("BIST");
    const [piyasaSecenekleri, setPiyasaSecenekleri] = useState(["BIST","Kripto"]);
    const [detayAcik,  setDetayAcik]  = useState(null);
    const intervalRef = useRef(null);

    // ── Kriter seçeneklerini DB'den al ───────────────────────────────────────
    useEffect(() => {
        getLookupPiyasalar().then(p => { if (p?.length) setPiyasaSecenekleri(p); }).catch(e => console.log("Lookup hatası:", e));
    }, []);

    // İşlemler menüsü / seçim
    const [secilenler,           setSecilenler]           = useState(new Set());
    const [islemMenuAcik,        setIslemMenuAcik]        = useState(false);
    const [dogrulukKaydediliyor, setDogrulukKaydediliyor]  = useState(false);
    const [dogrulukPopup,        setDogrulukPopup]         = useState(null); // null | "yukleniyor" | "tamam"
    const [dogrulukSonuc,        setDogrulukSonuc]         = useState(null);

    // Puanlamaya Gönder popup
    const [puanlamaPopupAdim, setPuanlamaPopupAdim] = useState(null); // null | "kriter" | "onay"
    const [puanlamaVade,      setPuanlamaVade]      = useState("Kısa Vade (1-10 gün)");
    const [puanlamaMinPuan,   setPuanlamaMinPuan]   = useState(0);

    // Çoklu Taramaya Gönder popup
    const [cokluPopup,     setCokluPopup]     = useState(false);
    const [cokluOnayPopup, setCokluOnayPopup] = useState(false);
    const [cokluSecModul,  setCokluSecModul]  = useState(null);
    const [cokluFiltre,    setCokluFiltre]    = useState(null);
    const [cokluPeriyotlar,setCokluPeriyotlar]= useState([]);

    // Sonuç Filtre Kartı: RS Skoru / Faz / Hacim Teyit Skoru
    const [filtreMinRs,          setFiltreMinRs]          = useState(0);
    const [filtreFaz,            setFiltreFaz]            = useState(new Set());
    const [filtreMinHacimTeyit,  setFiltreMinHacimTeyit]  = useState(0);

    // Alarm popup
    const [alarmPopup,        setAlarmPopup]        = useState(false);
    const [alarmEndeksler,    setAlarmEndeksler]    = useState([]);
    const [hedefFiyatlar,     setHedefFiyatlar]     = useState({});
    const [alarmMail,         setAlarmMail]         = useState(false);
    const [alarmBildirim,     setAlarmBildirim]     = useState(true);
    const [alarmKaydediliyor, setAlarmKaydediliyor] = useState(false);

    // Geçmiş Simülasyonu popup (2 adım: kriter -> onay)
    const [gecmisPopupAdim, setGecmisPopupAdim] = useState(null);
    const [gecmisSimTarihi, setGecmisSimTarihi] = useState(() => {
        const d = new Date(); d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().split("T")[0];
    });
    const [gecmisRefFiyat, setGecmisRefFiyat] = useState("Bugünkü");

    // ── Tarama Başlat ────────────────────────────────────────────────────────
    const taramaBaslat = async () => {
        setSonuc(null);
        setSecilenler(new Set());
        setCalisiyor(true);
        setIlerleme(0);
        setTahminiSure(null);
        setGecenSure(0);
        setBaslangicZamani(Date.now());
        try {
            const token = await getToken();
            await axios.post(`${API_URL}/scan/sektorel-rotasyon-tara`, { mod: piyasa }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Sektörel Güç Rotasyonu başlatıldı!");
            intervalRef.current = setInterval(durumSorgula, 3000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata oluştu");
            setCalisiyor(false);
        }
    };

    // Geçen süre sayacı
    useEffect(() => {
        if (!calisiyor || !baslangicZamani) { setGecenSure(0); return; }
        const timer = setInterval(() => {
            setGecenSure(Math.floor((Date.now() - baslangicZamani) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, [calisiyor, baslangicZamani]);

    const durumSorgula = async () => {
        try {
            const token = await getToken();
            if (!token) { clearInterval(intervalRef.current); return; }
            const res = await axios.get(`${API_URL}/scan/sektorel-rotasyon-durum`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDurum(res.data);
            setIlerleme(res.data.ilerleme || 0);
            setTahminiSure(res.data.tahmini_sure);
            if (!res.data.calisiyor) {
                clearInterval(intervalRef.current);
                setCalisiyor(false);
                if (res.data.sonuc) {
                    setSonuc(res.data.sonuc);
                    if (res.data.sonuc.hata) {
                        toast.error(res.data.sonuc.hata);
                    } else {
                        const lider = res.data.sonuc.sonuclar?.[0];
                        toast.success(lider ? `Analiz tamamlandı! Lider ${piyasa === "BIST" ? "sektör" : "kategori"}: ${lider.Ad}` : "Analiz tamamlandı!");
                    }
                } else if (res.data.hata) {
                    toast.error("Analiz başarısız: " + res.data.hata);
                }
            }
        } catch (e) {
            console.log("Durum hatası:", e);
        }
    };

    useEffect(() => () => clearInterval(intervalRef.current), []);

    const siralamaToggle = (kol) => {
        if (siralamaKol === kol) setSiralamaYon(y => y === "asc" ? "desc" : "asc");
        else { setSiralamaKol(kol); setSiralamaYon("desc"); }
    };

    const siralanmisVeri = sonuc?.sonuclar ? [...sonuc.sonuclar].sort((a, b) => {
        if (!siralamaKol) return 0;
        const av = a[siralamaKol]; const bv = b[siralamaKol];
        if (av == null) return 1; if (bv == null) return -1;
        const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "tr");
        return siralamaYon === "asc" ? cmp : -cmp;
    }) : [];

    const kolonlar = [
        "Endeks", "Ad", "RS Skoru", "Rank", "Rank Değişim", "Faz",
        "Hacim Teyit Yön", "Hacim Teyit Skoru", "Hacim Uyumlu", "Üye Hisse Sayısı",
    ];

    // Tablo başlıklarının GÖRÜNTÜ metni — gerçek alan adı (kolonlar) sabit
    // kalır (backend her iki piyasa için aynı anahtarı kullanıyor), sadece
    // kullanıcıya gösterilen etiket piyasaya göre değişir.
    const kolonEtiket = (h) => {
        if (h === "Endeks") return piyasa === "BIST" ? "Endeks" : "Kategori";
        if (h === "Üye Hisse Sayısı") return piyasa === "BIST" ? "Üye Hisse Sayısı" : "Üye Coin Sayısı";
        return h;
    };
    const KOLON_BILGI = {
        "RS Skoru": "RS Skoru", "Rank Değişim": "Rank Değişim", "Faz": "Faz",
        "Hacim Teyit Yön": "Hacim Teyit Yön", "Hacim Teyit Skoru": "Hacim Teyit Skoru", "Hacim Uyumlu": "Hacim Uyumlu",
    };

    // ── Filtre Kartı: RS Skoru / Faz / Hacim Teyit Skoru ──────
    const FAZ_SECENEKLERI = ["Güçlü Güçlenme","Güçlenme","Nötr","Zayıflama","Güçlü Zayıflama"];
    const toggleFiltreFaz = v => setFiltreFaz(p=>{const s=new Set(p);s.has(v)?s.delete(v):s.add(v);return s;});
    const filtreAktif = filtreMinRs>0 || filtreFaz.size>0 || filtreMinHacimTeyit>0;

    const gorunenVeri = siralanmisVeri.filter(r => {
        if (filtreMinRs>0 && (r["RS Skoru"]||0) < filtreMinRs) return false;
        if (filtreFaz.size>0 && !filtreFaz.has(r["Faz"])) return false;
        if (filtreMinHacimTeyit>0 && (r["Hacim Teyit Skoru"]||0) < filtreMinHacimTeyit) return false;
        return true;
    });

    // ── Satır seçimi (checkbox) ──────────────────────────────────────────────
    const toggleSec = (endeks) => {
        setSecilenler(prev => {
            const s = new Set(prev);
            s.has(endeks) ? s.delete(endeks) : s.add(endeks);
            return s;
        });
    };
    const tumSecili = gorunenVeri.length > 0 && gorunenVeri.every(r => secilenler.has(r["Endeks"]));
    const toggleTum = () => {
        tumSecili ? setSecilenler(new Set()) : setSecilenler(new Set([...secilenler, ...gorunenVeri.map(r => r["Endeks"])]));
    };

    const seciliSatirlar = () => siralanmisVeri.filter(r => secilenler.has(r["Endeks"]));

    // ── Alarm Ekle ────────────────────────────────────────────────────────────
    const alarmAc = () => {
        if (secilenler.size === 0) { toast.error(`Önce en az bir ${piyasa === "BIST" ? "sektör" : "kategori"} seçin`); return; }
        const satirlar = seciliSatirlar();
        setAlarmEndeksler(satirlar.map(r => ({ hisse: r["Endeks"], fiyat: null })));
        setHedefFiyatlar({}); setAlarmMail(false); setAlarmBildirim(true);
        setAlarmPopup(true); setIslemMenuAcik(false);
    };

    const alarmKaydet = async () => {
        const eksik = alarmEndeksler.find(h => !hedefFiyatlar[h.hisse] || isNaN(parseFloat(hedefFiyatlar[h.hisse])));
        if (eksik) { toast.error(`${eksik.hisse} için hedef RS skoru girin`); return; }
        setAlarmKaydediliyor(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            for (const h of alarmEndeksler) {
                const hedef = parseFloat(hedefFiyatlar[h.hisse]);
                const { error } = await supabase.from("alerts").insert({
                    piyasa, sembol: h.hisse, para_birimi: piyasa === "BIST" ? "TL" : "USD",
                    fiyat: null, hedef_fiyat: hedef, potansiyel: null,
                    bildirim_mail: alarmMail, bildirim_popup: alarmBildirim, user_id: session.user.id,
                });
                if (error) throw error;
            }
            toast.success(`${alarmEndeksler.length} alarm eklendi!`);
            setAlarmPopup(false); setAlarmEndeksler([]);
        } catch (e) { toast.error("Alarm kaydedilemedi: " + e.message); }
        finally { setAlarmKaydediliyor(false); }
    };

    // ── Doğruluk Analizi ─────────────────────────────────────────────────────
    const dogrulukKaydet = async () => {
        if (secilenler.size === 0) { toast.error(`Önce en az bir ${piyasa === "BIST" ? "sektör" : "kategori"} seçin`); return; }
        setIslemMenuAcik(false);
        setDogrulukKaydediliyor(true);
        setDogrulukPopup("yukleniyor");
        setDogrulukSonuc(null);
        await new Promise(r => setTimeout(r, 50));
        try {
            const token = await getToken();
            const res = await axios.post(`${API_URL}/scan/sektorel-rotasyon-analiz/kaydet`, {
                mod: piyasa,
                secilenler: seciliSatirlar(),
            }, { headers: { Authorization: `Bearer ${token}` } });
            setDogrulukSonuc(res.data);
            setDogrulukPopup("tamam");
        } catch (e) {
            toast.error("Kaydedilemedi: " + (e.response?.data?.detail || e.message));
            setDogrulukPopup(null);
        } finally {
            setDogrulukKaydediliyor(false);
        }
    };

    // ── Geçmiş Simülasyonu ───────────────────────────────────────────────────
    const gecmisSimAc = () => {
        if (secilenler.size === 0) { toast.error(`Önce en az bir ${piyasa === "BIST" ? "sektör" : "kategori"} seçin`); return; }
        setIslemMenuAcik(false);
        setGecmisPopupAdim("kriter");
    };

    const gecmisSimOnay = () => {
        const endeksler = [...secilenler];
        navigate("/gecmis-simulasyon", { state: {
            kaynak:        "sektorel-rotasyon",
            kaynak_label:  "Sektörel Güç Rotasyonu",
            mod:           piyasa,
            para_birimi:   piyasa === "BIST" ? "TL" : "USD",
            sim_tarihi:    gecmisSimTarihi,
            ref_fiyat:     gecmisRefFiyat,
            hisse_listesi: endeksler,
            oto_baslat:    true,
        }});
        setGecmisPopupAdim(null);
    };

    // ── Rotasyon Haritası — basit SVG quadrant/akış görseli ──────────────────
    const RotasyonHaritasiGorsel = ({ harita, sektorMap }) => {
        if (!harita || harita.length === 0) return null;
        const genislik = 720, yukseklik = Math.max(220, harita.length * 56 + 40);
        return (
            <svg viewBox={`0 0 ${genislik} ${yukseklik}`} style={{ width: "100%", maxWidth: genislik, height: "auto" }}>
                {harita.map((h, i) => {
                    const y = 30 + i * 56;
                    const kaynakAd = sektorMap[h.kaynak_endeks]?.Ad;
                    const hedefAd  = sektorMap[h.hedef_endeks]?.Ad;
                    const guc = h.eslesme_gucu || 0;
                    const kalinlik = 2 + (guc / 100) * 6;
                    return (
                        <g key={i}>
                            <rect x={10} y={y - 16} width={230} height={32} rx={8} fill="#dc262622" stroke="#dc2626" />
                            <text x={20} y={y + 5} fontSize="12" fontWeight="700" fill="#f87171">
                                {h.kaynak_endeks}{kaynakAd ? ` · ${kaynakAd}` : ""} ({h.kaynak_rank_degisim})
                            </text>
                            <line x1={245} y1={y} x2={genislik - 245} y2={y}
                                stroke="#94a3b8" strokeWidth={kalinlik} markerEnd="url(#ok)" />
                            <text x={(245 + genislik - 245) / 2} y={y - 8} fontSize="10" fill="#94a3b8" textAnchor="middle">
                                güç: {guc}
                            </text>
                            <rect x={genislik - 240} y={y - 16} width={230} height={32} rx={8} fill="#16a34a22" stroke="#16a34a" />
                            <text x={genislik - 230} y={y + 5} fontSize="12" fontWeight="700" fill="#4ade80">
                                {h.hedef_endeks}{hedefAd ? ` · ${hedefAd}` : ""} (+{h.hedef_rank_degisim})
                            </text>
                        </g>
                    );
                })}
                <defs>
                    <marker id="ok" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                        <path d="M0,0 L8,4 L0,8 Z" fill="#94a3b8" />
                    </marker>
                </defs>
            </svg>
        );
    };

    const sektorMap = Object.fromEntries((sonuc?.sonuclar || []).map(r => [r["Endeks"], r]));

    return (
        <div style={{padding:"1.5rem",background:"#0f172a",minHeight:"100vh",color:"#e2e8f0"}}>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24}}>
                <span style={{fontSize:32}}>🔄</span>
                <div>
                    <h1 style={{margin:0,fontSize:22,fontWeight:800,color:"#e2e8f0"}}>Sektörel Güç Rotasyonu</h1>
                    <p style={{margin:0,fontSize:13,color:"#94a3b8",maxWidth:820,lineHeight:1.6}}>
                        {piyasa === "BIST" ? (
                            <>Hangi BIST sektörünün para çektiğini/kaybettiğini tarar. Göreceli Güç (RS — sektörün
                            BIST100'e karşı performansı) ana sinyal, üye hisselerin Hacim Akışı ortalaması teyit
                            katmanıdır. RS skorunun kendi trendi (momentum) öncü sinyal sağlar; rank değişimleri
                            "olası para geçişi" haritası olarak gösterilir.</>
                        ) : (
                            <>Hangi kripto kategorisinin (Layer 1, DeFi, Meme, AI, ...) para çektiğini/kaybettiğini
                            tarar. Göreceli Güç (RS — kategorinin Layer 1'e karşı performansı) ana sinyal, üye
                            coinlerin Binance hacim verisi teyit katmanıdır. RS skorunun kendi trendi (momentum)
                            öncü sinyal sağlar; rank değişimleri "olası para geçişi" haritası olarak gösterilir.</>
                        )}
                    </p>
                </div>
            </div>

            {/* Kontrol Paneli */}
            <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:12,
                padding:"16px 20px",marginBottom:20,display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end",
                opacity:calisiyor?0.5:1,pointerEvents:calisiyor?"none":"auto",transition:"opacity 0.3s"}}>

                <fieldset style={{border:"1px solid #334155",borderRadius:8,padding:"6px 12px 10px",margin:0}}>
                    <legend style={{fontSize:11,color:"#94a3b8",fontWeight:600,padding:"0 4px",letterSpacing:"0.05em"}}>PİYASA</legend>
                    <div style={{display:"flex",gap:6}}>
                        {piyasaSecenekleri.map(p => (
                            <button key={p} onClick={()=>setPiyasa(p)} style={{
                                padding:"5px 14px",borderRadius:6,fontWeight:600,fontSize:13,cursor:"pointer",
                                border:`2px solid ${piyasa===p?"#3b82f6":"#334155"}`,
                                background:piyasa===p?"#3b82f622":"transparent",
                                color:piyasa===p?"#3b82f6":"#94a3b8"}}>{p}</button>
                        ))}
                    </div>
                </fieldset>

                <button onClick={taramaBaslat} style={{
                    padding:"10px 28px",borderRadius:8,fontWeight:700,fontSize:14,cursor:"pointer",
                    background:"linear-gradient(135deg,#4c1d95,#7c3aed)",
                    border:"none",color:"white",alignSelf:"flex-end"}}>
                    🔍 Tara
                </button>
            </div>

            {/* Uyarı + İlerleme */}
            {calisiyor && (
                <>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,
                        padding:"10px 16px",borderRadius:8,background:"#422006",border:"1px solid #92400e"}}>
                        <span style={{fontSize:16}}>⚠️</span>
                        <span style={{fontSize:13,color:"#fde68a",fontWeight:600}}>
                            Tarama tamamlanıncaya kadar sayfayı kapatmayın
                        </span>
                    </div>
                    <TaramaIlerlemePaneli
                        mesaj={durum?.mesaj}
                        ilerleme={ilerleme}
                        gecen={gecenSure}
                        tahminiSure={tahminiSure}
                    />
                </>
            )}

            {/* Bilgilendirme (DB cache boşsa) */}
            {sonuc?.hata && (
                <div style={{
                    background: "#422006", border: "1px solid #f59e0b", borderRadius: "10px",
                    padding: "1rem 1.25rem", marginBottom: "1rem", color: "#fde68a", fontSize: "13px",
                }}>
                    ℹ️ {sonuc.hata}
                </div>
            )}

            {/* Lider Sektör/Kategori Özet Kartı */}
            {sonuc?.sonuclar?.length > 0 && (
                <div style={styles.ozetKart}>
                    <div style={styles.ozetGenel}>
                        <span style={styles.ozetEtiket}>{piyasa === "BIST" ? "LİDER SEKTÖR" : "LİDER KATEGORİ"}</span>
                        <span style={{ ...styles.ozetDeger, color: fazRenk(siralanmisVeri[0]?.Faz) }}>
                            {siralanmisVeri[0]?.Ad}
                        </span>
                        <span style={styles.ozetPuan}>RS {siralanmisVeri[0]?.["RS Skoru"]}/100 · {siralanmisVeri[0]?.Faz}</span>
                    </div>
                    <div style={styles.ozetDetayGrid}>
                        {siralanmisVeri.slice(0, 4).map(r => (
                            <div key={r["Endeks"]} style={styles.ozetHucre}>
                                <span style={styles.ozetHucreEtiket}>#{r["Rank"]} {r["Endeks"]}</span>
                                <span style={{ color: skorRenk(r["RS Skoru"]), fontWeight: 700 }}>
                                    {r["RS Skoru"]} · {r["Faz"]}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "8px" }}>
                        Son hesaplama: {siralanmisVeri[0]?.Tarih}
                        {" · "}
                        {sonuc.tarih && new Date(sonuc.tarih).toLocaleString("tr-TR")}
                    </div>
                </div>
            )}

            {/* Rotasyon Haritası */}
            {sonuc?.rotasyon_haritasi?.length > 0 && (
                <div style={styles.ozetKart}>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#93c5fd", marginBottom: "0.75rem" }}>
                        🗺️ Rotasyon Haritası — Olası Para Geçişleri
                    </div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "1rem" }}>
                        Rank'ı düşen {piyasa === "BIST" ? "sektörden" : "kategoriden"} (kırmızı) rank'ı yükselen
                        {piyasa === "BIST" ? " sektöre" : " kategoriye"} (yeşil) — istatistiksel
                        korelasyon değil, rank hareketi büyüklüğüne göre sezgisel eşleştirmedir.
                    </div>
                    <RotasyonHaritasiGorsel harita={sonuc.rotasyon_haritasi} sektorMap={sektorMap} />
                </div>
            )}

            {/* Sonuç Özeti + İşlemler */}
            {sonuc?.sonuclar?.length > 0 && (
                <div style={{display:"flex",gap:16,background:"#1e293b",padding:"12px 20px",borderRadius:8,
                    marginBottom:16,fontSize:13,flexWrap:"wrap",alignItems:"center",border:"1px solid #334155"}}>

                    <div style={{position:"relative"}}>
                        <button onClick={() => setIslemMenuAcik(v => !v)} style={{
                            padding:"6px 16px",borderRadius:8,border:"1px solid #334155",
                            background:"#1e293b",cursor:"pointer",fontSize:13,fontWeight:500,
                            color:"#e2e8f0",display:"flex",alignItems:"center",gap:5}}>
                            İşlemler ▾ {secilenler.size > 0 && `(${secilenler.size})`}
                        </button>
                        {islemMenuAcik && (
                            <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,background:"#1e293b",
                                border:"1px solid #334155",borderRadius:10,
                                boxShadow:"0 8px 24px rgba(0,0,0,0.4)",zIndex:100,minWidth:260,overflow:"visible"}}>
                                {[
                                    { label: dogrulukKaydediliyor ? "⏳ Kaydediliyor..." : "✅ Doğruluk Analizine Gönder",
                                      aktif: !dogrulukKaydediliyor, fn: dogrulukKaydet },
                                    { label: "🔮 Geçmiş Simülasyonuna Gönder", aktif: false, fn: gecmisSimAc },
                                    { label: "⏰ Alarm Ekle", aktif: true, fn: alarmAc },
                                    { label: "🔎 İzleme Listesine Ekle", aktif: false, fn: () => {} },
                                ].map(item => (
                                    <button key={item.label} onClick={item.aktif ? item.fn : undefined} disabled={!item.aktif}
                                        style={{display:"block",width:"100%",textAlign:"left",padding:"10px 16px",
                                            border:"none",background:"none",fontSize:13,borderBottom:"1px solid #334155",
                                            cursor:item.aktif?"pointer":"default",color:!item.aktif?"#94a3b8":"#e2e8f0"}}>
                                        {item.label}
                                    </button>
                                ))}
                                <IndirMenusu veriler={() => sonuc?.sonuclar || []} setIslemMenuAcik={setIslemMenuAcik} />
                            </div>
                        )}
                    </div>

                    <div style={{width:1,height:20,background:"#334155"}}/>
                    <span>📊 <strong>{piyasa}</strong></span>
                    <span>✨ <strong>{sonuc.sonuclar.length}</strong> {piyasa === "BIST" ? "sektör" : "kategori"}</span>
                    <span style={{color:"#94a3b8",fontSize:12,marginLeft:"auto"}}>
                        {sonuc.tarih && new Date(sonuc.tarih).toLocaleString("tr-TR")}
                    </span>
                </div>
            )}

            {/* Filtre Kartı: RS Skoru / Faz / Hacim Teyit Skoru */}
            {siralanmisVeri.length > 0 && (
                <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:12,
                    padding:"12px 20px",marginBottom:12,display:"flex",gap:20,flexWrap:"wrap",alignItems:"center"}}>

                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:11,fontWeight:700,color:"#94a3b8",letterSpacing:"0.05em",display:"flex",alignItems:"center"}}>RS SKORU<BilgiIkonu alan="RS Skoru"/></span>
                        {[0,50,55,60,65].map(v => (
                            <button key={v} onClick={()=>setFiltreMinRs(v)} style={{
                                padding:"4px 10px",borderRadius:20,fontSize:12,cursor:"pointer",
                                fontWeight:filtreMinRs===v?700:400,
                                border:`2px solid ${filtreMinRs===v?"#3b82f6":"#334155"}`,
                                background:filtreMinRs===v?"#3b82f622":"transparent",
                                color:filtreMinRs===v?"#3b82f6":"#94a3b8"}}>{v===0?"Tümü":`${v}+`}</button>
                        ))}
                    </div>

                    <div style={{width:1,height:20,background:"#334155"}}/>

                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:11,fontWeight:700,color:"#94a3b8",letterSpacing:"0.05em",display:"flex",alignItems:"center"}}>FAZ<BilgiIkonu alan="Faz"/></span>
                        {FAZ_SECENEKLERI.map(v => {
                            const aktif = filtreFaz.has(v);
                            return (
                                <button key={v} onClick={()=>toggleFiltreFaz(v)} style={{
                                    padding:"4px 12px",borderRadius:20,fontSize:12,cursor:"pointer",
                                    fontWeight:aktif?700:400,
                                    border:`2px solid ${aktif?fazRenk(v):"#334155"}`,
                                    background:aktif?fazRenk(v)+"22":"transparent",
                                    color:aktif?fazRenk(v):"#94a3b8"}}>{v}</button>
                            );
                        })}
                    </div>

                    <div style={{width:1,height:20,background:"#334155"}}/>

                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:11,fontWeight:700,color:"#94a3b8",letterSpacing:"0.05em",display:"flex",alignItems:"center"}}>HACİM TEYİT SKORU<BilgiIkonu alan="Hacim Teyit Skoru"/></span>
                        {[0,50,60,70].map(v => (
                            <button key={v} onClick={()=>setFiltreMinHacimTeyit(v)} style={{
                                padding:"4px 10px",borderRadius:20,fontSize:12,cursor:"pointer",
                                fontWeight:filtreMinHacimTeyit===v?700:400,
                                border:`2px solid ${filtreMinHacimTeyit===v?"#3b82f6":"#334155"}`,
                                background:filtreMinHacimTeyit===v?"#3b82f622":"transparent",
                                color:filtreMinHacimTeyit===v?"#3b82f6":"#94a3b8"}}>{v===0?"Tümü":`${v}+`}</button>
                        ))}
                    </div>

                    <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:12,color:"#94a3b8"}}>
                            <strong style={{color:"#e2e8f0"}}>{gorunenVeri.length}</strong> / {siralanmisVeri.length} sonuç
                        </span>
                        {filtreAktif && (
                            <button onClick={()=>{setFiltreMinRs(0);setFiltreFaz(new Set());setFiltreMinHacimTeyit(0);}}
                                style={{padding:"4px 12px",borderRadius:20,fontSize:12,cursor:"pointer",
                                    border:"1px solid #dc262666",background:"#dc262611",color:"#dc2626"}}>
                                ✕ Temizle
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Tablo */}
            {gorunenVeri.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                    <table style={styles.tablo}>
                        <thead>
                            <tr style={styles.thead}>
                                <th style={{ ...styles.th, width: "36px" }}>
                                    <input type="checkbox" checked={tumSecili} onChange={toggleTum} />
                                </th>
                                {kolonlar.map(h => (
                                    <th key={h}
                                        style={{ ...styles.th, cursor: "pointer", userSelect: "none" }}
                                        onClick={() => siralamaToggle(h)}>
                                        {kolonEtiket(h)}{KOLON_BILGI[h] && <BilgiIkonu alan={KOLON_BILGI[h]}/>} {siralamaKol === h ? (siralamaYon === "asc" ? "↑" : "↓") : ""}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {gorunenVeri.map((r, i) => (
                                <tr key={r["Endeks"]} onClick={() => setDetayAcik(r)}
                                    style={{ cursor:"pointer", background: secilenler.has(r["Endeks"]) ? "#3b82f622" : i % 2 === 0 ? "#1e293b" : "#182234" }}>
                                    <td style={styles.td} onClick={e => e.stopPropagation()}>
                                        <input type="checkbox" checked={secilenler.has(r["Endeks"])}
                                            onChange={() => toggleSec(r["Endeks"])} />
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: "bold" }}>{r["Endeks"]}</td>
                                    <td style={styles.td}>{r["Ad"]}</td>
                                    <td style={{ ...styles.td, color: skorRenk(r["RS Skoru"]), fontWeight: "700" }}>
                                        {r["RS Skoru"]}
                                    </td>
                                    <td style={styles.td}>#{r["Rank"]}</td>
                                    <td style={{
                                        ...styles.td,
                                        color: (r["Rank Değişim"] || 0) > 0 ? "#16a34a" : (r["Rank Değişim"] || 0) < 0 ? "#dc2626" : "#94a3b8",
                                        fontWeight: "600",
                                    }}>
                                        {r["Rank Değişim"] > 0 ? `▲ +${r["Rank Değişim"]}` : r["Rank Değişim"] < 0 ? `▼ ${r["Rank Değişim"]}` : "—"}
                                    </td>
                                    <td style={{ ...styles.td, color: fazRenk(r["Faz"]), fontWeight: "700" }}>
                                        {r["Faz"]}
                                    </td>
                                    <td style={{
                                        ...styles.td,
                                        color: r["Hacim Teyit Yön"] === "Pozitif" ? "#16a34a"
                                             : r["Hacim Teyit Yön"] === "Negatif" ? "#dc2626" : "#94a3b8",
                                        fontWeight: "600",
                                    }}>
                                        {r["Hacim Teyit Yön"]}
                                    </td>
                                    <td style={styles.td}>{r["Hacim Teyit Skoru"] ?? "—"}</td>
                                    <td style={{
                                        ...styles.td,
                                        color: r["Hacim Uyumlu"]?.includes("Evet") ? "#16a34a" : "#94a3b8",
                                    }}>
                                        {r["Hacim Uyumlu"]}
                                    </td>
                                    <td style={styles.td}>{r["Üye Hisse Sayısı"]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {sonuc && !sonuc.hata && sonuc?.sonuclar?.length === 0 && (
                <div style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}>
                    Veri bulunamadı.
                </div>
            )}

            {/* Detay Paneli */}
            {detayAcik && <DetayPanel r={detayAcik} piyasa={piyasa} onKapat={() => setDetayAcik(null)} />}

            {/* Doğruluk Popup */}
            {dogrulukPopup && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
                    display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,
                        padding:"2rem",width:420,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)"}}>
                        {dogrulukPopup==="yukleniyor"?(
                            <>
                                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                                    <span style={{fontSize:24}}>✅</span>
                                    <div>
                                        <div style={{fontSize:16,fontWeight:700,color:"#e2e8f0"}}>Doğruluk Analizi</div>
                                        <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>Veriler kaydediliyor...</div>
                                    </div>
                                </div>
                                <HareketliBar/>
                                <div style={{fontSize:13,color:"#94a3b8",textAlign:"center"}}>Kaydediliyor...</div>
                            </>
                        ):(
                            <>
                                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                                    <span style={{fontSize:28}}>✅</span>
                                    <div style={{fontSize:16,fontWeight:700,color:"#e2e8f0"}}>Kayıt Tamamlandı</div>
                                </div>
                                <div style={{background:"#16a34a22",border:"1px solid #16a34a44",
                                    borderRadius:8,padding:"14px 16px",marginBottom:20,fontSize:14,color:"#16a34a",lineHeight:1.6}}>
                                    Seçilenler <strong>"Doğruluk Analizi"</strong> veri tabanına kaydedildi.
                                    {dogrulukSonuc && (
                                        <div style={{marginTop:8,fontSize:12,color:"#94a3b8"}}>
                                            ✔ {dogrulukSonuc.eklenen} yeni · {dogrulukSonuc.atlanan} mevcut
                                        </div>
                                    )}
                                </div>
                                <div style={{display:"flex",justifyContent:"flex-end"}}>
                                    <button onClick={()=>{setDogrulukPopup(null);setDogrulukSonuc(null);}}
                                        style={{padding:"8px 28px",borderRadius:8,border:"none",
                                            background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",
                                            color:"white",cursor:"pointer",fontSize:14,fontWeight:600}}>
                                        Tamam
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Puanlama Kriter */}
            {puanlamaPopupAdim === "kriter" && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,padding:"2rem",width:440,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                        <div style={{fontSize:18,fontWeight:700,color:"#e2e8f0",marginBottom:12}}>⭐ Puanlamaya Gönder</div>
                        <p style={{fontSize:13,color:"#94a3b8",marginBottom:"1.25rem"}}>
                            <strong>{secilenler.size}</strong> {piyasa === "BIST" ? "sektör" : "kategori"} seçildi. Puanlama kriterlerini belirleyin.
                        </p>
                        <div style={{display:"flex",flexDirection:"column",gap:"1rem",marginBottom:"1.25rem"}}>
                            <div>
                                <label style={{fontSize:12,fontWeight:600,color:"#93c5fd",display:"block",marginBottom:6}}>Vade</label>
                                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                    {(piyasa === "Kripto"
                                        ? ["Gün İçi","Kısa Vade (1-10 gün)","Orta Vade (11-60 gün)","Uzun Vade (61+ gün)","Kaldıraçlı İşlem"]
                                        : ["Gün İçi","Kısa Vade (1-10 gün)","Orta Vade (11-60 gün)","Uzun Vade (61+ gün)"]
                                    ).map(v => (
                                        <button key={v} type="button" onClick={() => setPuanlamaVade(v)} style={{
                                            padding:"6px 12px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",
                                            border:`2px solid ${puanlamaVade===v?"#3b82f6":"#334155"}`,
                                            background:puanlamaVade===v?"#3b82f622":"transparent",
                                            color:puanlamaVade===v?"#3b82f6":"#e2e8f0"}}>{v}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label style={{fontSize:12,fontWeight:600,color:"#93c5fd",display:"block",marginBottom:6}}>Min Puan</label>
                                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                    {[0,50,60,65,70,75,80].map(v => (
                                        <button key={v} type="button" onClick={() => setPuanlamaMinPuan(v)} style={{
                                            padding:"5px 10px",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",
                                            border:`2px solid ${puanlamaMinPuan===v?"#eab308":"#334155"}`,
                                            background:puanlamaMinPuan===v?"#eab30822":"transparent",
                                            color:puanlamaMinPuan===v?"#eab308":"#94a3b8"}}>{v===0?"Tümü":`${v}+`}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                            <button onClick={() => setPuanlamaPopupAdim(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>İptal</button>
                            <button onClick={() => setPuanlamaPopupAdim("onay")} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:13,fontWeight:600}}>Devam Et →</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Puanlama Onay */}
            {puanlamaPopupAdim === "onay" && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,padding:"2rem",width:440,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                        <div style={{fontSize:18,fontWeight:700,color:"#e2e8f0",marginBottom:12}}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{background:"#422006",border:"1px solid #92400e",borderRadius:8,padding:"1rem",marginBottom:20,fontSize:13,color:"#fde68a",lineHeight:1.7}}>
                            Başka bir sayfaya yönlendiriliyorsunuz. <strong>Ekrandaki tarama sonuçları kaybolacak.</strong>
                            <br/><br/>Seçili <strong>{secilenler.size} {piyasa === "BIST" ? "sektör" : "kategori"}</strong> için <strong>Puanlama ve Tahmin</strong> taraması otomatik olarak başlatılacaktır. Emin misiniz?
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20,fontSize:12}}>
                            {[["Piyasa",piyasa],["Para Birimi",piyasa==="BIST"?"TL":"USD"],["Vade",puanlamaVade],
                              ["Min Puan",puanlamaMinPuan===0?"0+ (Tümü)":`${puanlamaMinPuan}+`],
                              [piyasa === "BIST" ? "Sektör Sayısı" : "Kategori Sayısı",`${secilenler.size} ${piyasa === "BIST" ? "sektör" : "kategori"}`],
                            ].map(([k,v]) => (
                                <div key={k} style={{display:"flex",justifyContent:"space-between",background:"#0f172a",borderRadius:6,padding:"6px 10px"}}>
                                    <span style={{color:"#94a3b8"}}>{k}:</span>
                                    <strong style={{color:"#e2e8f0"}}>{v}</strong>
                                </div>
                            ))}
                        </div>
                        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                            <button onClick={() => setPuanlamaPopupAdim("kriter")} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>← Geri</button>
                            <button onClick={() => setPuanlamaPopupAdim(null)} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>Hayır, İptal</button>
                            <button onClick={() => {
                                const hisseListesi = Array.from(secilenler);
                                setPuanlamaPopupAdim(null);
                                navigate("/puanlama-tahmin", {
                                    state: {
                                        kaynak: "sektorel-rotasyon",
                                        piyasa, para_birimi: piyasa==="BIST"?"TL":"USD", vade: puanlamaVade, min_puan: puanlamaMinPuan,
                                        hisse_listesi: hisseListesi, oto_baslat: true,
                                    }
                                });
                            }} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:13,fontWeight:600}}>Evet, Başlat 🚀</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Çoklu Taramaya Gönder */}
            {cokluPopup && (
                <CokluTaramaGonderPopup
                    piyasa={piyasa}
                    paraBirimi={piyasa==="BIST"?"TL":"USD"}
                    secilenSayisi={secilenler.size}
                    secilenModul={cokluSecModul}
                    filtre={cokluFiltre}
                    periyotlar={cokluPeriyotlar}
                    onModulSec={m=>{
                        setCokluSecModul(m);setCokluFiltre(defaultCokluFiltreler(m,piyasa));
                        const dbKey=MODUL_KEY_MAP[m];
                        if(dbKey) getLookupPeriyotlar(dbKey).then(list=>setCokluPeriyotlar(list)).catch(()=>setCokluPeriyotlar([]));
                        else setCokluPeriyotlar([]);
                    }}
                    onFiltreDegis={setCokluFiltre}
                    onIptal={()=>{setCokluPopup(false);setCokluSecModul(null);setCokluFiltre(null);}}
                    onBaslat={()=>{if(!cokluSecModul||!cokluFiltre){toast.error("Modül seçin");return;}setCokluPopup(false);setCokluOnayPopup(true);}}/>
            )}

            {/* Çoklu Tarama — Onay */}
            {cokluOnayPopup && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
                    <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:14,padding:"1.75rem",width:420,maxWidth:"95vw",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                        <div style={{fontSize:17,fontWeight:700,color:"#e2e8f0",marginBottom:12}}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{background:"#422006",border:"1px solid #92400e",borderRadius:8,padding:"1rem",marginBottom:20,fontSize:13,color:"#fde68a",lineHeight:1.7}}>
                            Başka bir sayfaya yönlendiriliyorsunuz. <strong>Bu sayfadaki tarama sonuçları kaybolacak.</strong>
                            <br/><br/>Seçili <strong>{secilenler.size} {piyasa === "BIST" ? "sektör" : "kategori"}</strong> ile <strong>{cokluSecModul}</strong> taraması Çoklu Tarama'da otomatik başlatılacaktır.
                            <br/><br/>Devam etmek istiyor musunuz?
                        </div>
                        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                            <button onClick={()=>{setCokluOnayPopup(false);setCokluPopup(true);}} style={{padding:"8px 18px",borderRadius:8,border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:13,color:"#94a3b8"}}>← Geri Dön</button>
                            <button onClick={()=>{
                                const hisseListesi = Array.from(secilenler);
                                setCokluOnayPopup(false);
                                navigate("/coklu-tarama",{state:{
                                    kaynak:"sektorel-rotasyon",piyasa,paraBirimi:piyasa==="BIST"?"TL":"USD",
                                    kaynakModul:"Sektörel Güç Rotasyonu",
                                    kaynakSonucHisseler:hisseListesi,
                                    modul:cokluSecModul,filtre:cokluFiltre,hisseListesi,oto_baslat:true,
                                }});
                            }} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"white",cursor:"pointer",fontSize:13,fontWeight:600}}>Evet, Devam Et →</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Alarm Popup ──────────────────────────────────────────────────── */}
            {alarmPopup && (
                <div style={styles.modalArkaplan}>
                    <div style={styles.modalKutu}>
                        <h3 style={{ margin: "0 0 0.75rem", fontSize: "16px", color: "#93c5fd" }}>⏰ Alarm Ekle</h3>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {alarmEndeksler.map(h => (
                                <div key={h.hisse} style={{ background: "#182234", borderRadius: 8, padding: "10px 12px", border: "1px solid #334155" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                        <strong style={{ fontSize: 14 }}>{h.hisse}</strong>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ color: "#94a3b8", fontSize: 12, minWidth: 110 }}>Hedef RS Skoru:</span>
                                        <input type="number" value={hedefFiyatlar[h.hisse] || ""}
                                            onChange={e => setHedefFiyatlar(prev => ({ ...prev, [h.hisse]: e.target.value }))}
                                            style={{ flex: 1, padding: "4px 8px", borderRadius: 6, border: "1px solid #475569", fontSize: 13, textAlign: "right" }}
                                            placeholder="0-100" />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <div style={{ color: "#94a3b8", marginBottom: 6, fontSize: 12 }}>Bildirim Tercihi:</div>
                            <div style={{ display: "flex", gap: 16 }}>
                                {[["Mail", alarmMail, setAlarmMail], ["Pop-up", alarmBildirim, setAlarmBildirim]].map(([ad, val, setter]) => (
                                    <label key={ad} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                                        <input type="checkbox" checked={val} onChange={e => setter(e.target.checked)} /> {ad}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: "1.25rem", justifyContent: "flex-end" }}>
                            <button onClick={() => { setAlarmPopup(false); setAlarmEndeksler([]); }}
                                style={styles.modalIptal}>Vazgeç</button>
                            <button onClick={alarmKaydet} disabled={alarmKaydediliyor}
                                style={{ ...styles.modalOnay, background: alarmKaydediliyor ? "#94a3b8" : "#1e3a5f" }}>
                                {alarmKaydediliyor ? "Kaydediliyor..." : "Kaydet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Geçmiş Simülasyonu Popup — Adım 1: Kriter ───────────────────────── */}
            {gecmisPopupAdim === "kriter" && (
                <div style={styles.modalArkaplan}>
                    <div style={styles.modalKutuKoyu}>
                        <div style={{ fontSize: "18px", fontWeight: "700", color: "#e2e8f0", marginBottom: "0.75rem" }}>
                            🔮 Geçmiş Simülasyonu
                        </div>
                        <div style={{ fontSize: "13px", color: "#94a3b8", marginBottom: "1rem" }}>
                            <strong style={{ color: "#e2e8f0" }}>{secilenler.size}</strong> {piyasa === "BIST" ? "sektör" : "kategori"} seçildi.
                            Simülasyon kriterlerini belirleyin.
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "1.25rem" }}>
                            <label style={{ fontSize: "12px", color: "#94a3b8" }}>Piyasa</label>
                            <input value={piyasa} disabled style={styles.modalInputDisabled} />
                            <label style={{ fontSize: "12px", color: "#94a3b8" }}>Para Birimi</label>
                            <input value={piyasa === "BIST" ? "TL" : "USD"} disabled style={styles.modalInputDisabled} />
                            <label style={{ fontSize: "12px", color: "#94a3b8" }}>Simülasyon Tarihi</label>
                            <input type="date" value={gecmisSimTarihi} onChange={e => setGecmisSimTarihi(e.target.value)}
                                style={styles.modalInput} />
                            <label style={{ fontSize: "12px", color: "#94a3b8" }}>Referans Fiyat</label>
                            <select value={gecmisRefFiyat} onChange={e => setGecmisRefFiyat(e.target.value)}
                                style={styles.modalInput}>
                                <option>Bugünkü</option>
                                <option>Simülasyon Tarihindeki</option>
                            </select>
                        </div>
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button onClick={() => setGecmisPopupAdim(null)} style={styles.modalIptalKoyu}>Vazgeç</button>
                            <button onClick={() => { if (!gecmisSimTarihi) return; setGecmisPopupAdim("onay"); }}
                                style={styles.modalOnay}>Devam →</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Geçmiş Simülasyonu Popup — Adım 2: Onay ─────────────────────────── */}
            {gecmisPopupAdim === "onay" && (
                <div style={styles.modalArkaplan}>
                    <div style={styles.modalKutuKoyu}>
                        <div style={{ fontSize: "18px", fontWeight: "700", color: "#e2e8f0", marginBottom: "0.75rem" }}>
                            🔮 Geçmiş Simülasyonu — Onay
                        </div>
                        <div style={{
                            background: "#451a03", border: "1px solid #92400e", borderRadius: "8px", padding: "1rem",
                            marginBottom: "1.25rem", fontSize: "13px", color: "#fde68a", lineHeight: "1.7",
                        }}>
                            Bu sayfa temizlenerek <strong>Geçmiş Simülasyon</strong> sayfasına yönlendirileceksiniz.
                            <br /><br />Devam etmek istiyor musunuz?
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "1.25rem", fontSize: "12px" }}>
                            {[
                                ["Piyasa", piyasa],
                                ["Para Birimi", piyasa === "BIST" ? "TL" : "USD"],
                                ["Simülasyon Tarihi", (() => {
                                    const d = new Date(gecmisSimTarihi + "T00:00:00");
                                    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
                                })()],
                                ["Referans Fiyat", gecmisRefFiyat],
                                [piyasa === "BIST" ? "Sektör Sayısı" : "Kategori Sayısı",
                                 `${secilenler.size} ${piyasa === "BIST" ? "sektör" : "kategori"}`],
                            ].map(([k, v]) => (
                                <div key={k} style={{ display: "flex", justifyContent: "space-between", background: "#0f172a", borderRadius: "6px", padding: "6px 10px", border: "1px solid #334155" }}>
                                    <span style={{ color: "#94a3b8" }}>{k}:</span>
                                    <strong style={{ color: "#e2e8f0" }}>{v}</strong>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button onClick={() => setGecmisPopupAdim("kriter")} style={styles.modalIptalKoyu}>← Geri</button>
                            <button onClick={() => setGecmisPopupAdim(null)} style={styles.modalIptalKoyu}>Hayır, İptal</button>
                            <button onClick={gecmisSimOnay} style={styles.modalOnay}>Evet, Başlat 🚀</button>
                        </div>
                    </div>
                </div>
            )}

            {islemMenuAcik && <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setIslemMenuAcik(false)} />}
        </div>
    );
}

const styles = {
    sayfa:            { padding: "1.5rem" },
    baslik:           { marginBottom: "0.5rem", fontSize: "20px" },
    altBaslik:        { marginBottom: "1.5rem", fontSize: "13px", color: "#94a3b8", maxWidth: "760px", lineHeight: "1.6" },
    filtreSatiri:     { display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap",
                        background: "#1e293b", padding: "1.25rem", borderRadius: "12px",
                        marginBottom: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    filtreGrup:       { display: "flex", flexDirection: "column", gap: "4px" },
    etiket:           { fontSize: "12px", color: "#94a3b8", fontWeight: 600 },
    select:           { padding: "0.5rem 0.75rem", borderRadius: "8px", border: "1px solid #475569",
                        fontSize: "13px", background: "#1e293b", minWidth: "110px" },
    buton:            { padding: "0.55rem 1.25rem", color: "white", border: "none",
                        borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
    islemlerButon:    { padding: "5px 14px", borderRadius: "8px", border: "1px solid #334155",
                        background: "#1e293b", color: "white", cursor: "pointer", fontSize: "13px", fontWeight: 500 },
    islemlerMenu:     { position: "absolute", top: "calc(100% + 4px)", right: 0, background: "#1e293b",
                        border: "1px solid #334155", borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: "210px" },
    islemlerOge:      { display: "block", width: "100%", textAlign: "left", padding: "10px 14px",
                        border: "none", background: "none", fontSize: "13px", borderBottom: "1px solid #334155",
                        cursor: "pointer", color: "#e2e8f0" },
    ozetKart:         { background: "#1e293b", borderRadius: "12px", padding: "1.25rem 1.5rem",
                        marginBottom: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    ozetGenel:        { display: "flex", alignItems: "baseline", gap: "0.75rem", marginBottom: "1rem" },
    ozetEtiket:       { fontSize: "12px", color: "#94a3b8", fontWeight: 600, letterSpacing: "0.04em" },
    ozetDeger:        { fontSize: "26px", fontWeight: 800 },
    ozetPuan:         { fontSize: "14px", color: "#94a3b8" },
    ozetDetayGrid:    { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" },
    ozetHucre:        { display: "flex", flexDirection: "column", gap: "2px", background: "#182234",
                        borderRadius: "8px", padding: "8px 12px", border: "1px solid #334155" },
    ozetHucreEtiket:  { fontSize: "11px", color: "#94a3b8" },
    tablo:            { width: "100%", borderCollapse: "collapse", fontSize: "13px", background: "#1e293b",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderRadius: "8px", overflow: "hidden" },
    thead:            { background: "#0f172a" },
    th:               { padding: "0.6rem 0.8rem", color: "white", textAlign: "left",
                        whiteSpace: "nowrap", fontSize: "12px" },
    td:               { padding: "0.5rem 0.8rem", borderBottom: "1px solid #334155" },
    ilerlemeSarici:   { background: "#1e293b", padding: "1rem 1.25rem", borderRadius: "12px",
                        marginBottom: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    ilerlemeUst:      { display: "flex", justifyContent: "space-between", marginBottom: "8px",
                        fontSize: "14px", fontWeight: "500" },
    ilerlemeArkaplan: { background: "#334155", borderRadius: "99px", height: "10px", overflow: "hidden" },
    ilerlemeBar:      { height: "100%", background: "linear-gradient(90deg, #2563eb, #3b82f6)", borderRadius: "99px" },
    ilerlemeBilgi:    { marginTop: "6px", fontSize: "12px", color: "#94a3b8" },
    modalArkaplan:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
                        alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modalKutu:        { background: "#1e293b", borderRadius: "12px", padding: "1.5rem", width: "420px",
                        maxHeight: "80vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" },
    modalKutuKoyu:    { background: "#1e293b", borderRadius: "12px", padding: "1.5rem", width: "440px",
                        maxHeight: "85vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                        border: "1px solid #334155" },
    modalInput:       { padding: "0.5rem 0.75rem", borderRadius: "8px", border: "1px solid #334155",
                        background: "#0f172a", color: "#e2e8f0", fontSize: "13px" },
    modalInputDisabled:{ padding: "0.5rem 0.75rem", borderRadius: "8px", border: "1px solid #334155",
                        background: "#0f172a", color: "#64748b", fontSize: "13px" },
    modalIptal:       { padding: "6px 16px", borderRadius: 8, border: "1px solid #475569",
                        background: "#1e293b", cursor: "pointer", fontSize: 13 },
    modalIptalKoyu:   { padding: "8px 18px", borderRadius: "8px", border: "1px solid #334155",
                        background: "#0f172a", cursor: "pointer", fontSize: "13px", color: "#94a3b8" },
    modalOnay:        { padding: "8px 20px", borderRadius: "8px", border: "none", background: "#3b82f6",
                        color: "white", cursor: "pointer", fontSize: "13px", fontWeight: "600" },
};
